<template>
  <div class="content">
    <h1>Analytics</h1>
    <loading v-if="loading" text='Loading. Please wait...'/>
    <span v-show="error" class="help is-danger">{{error}}</span>

    <div class="analytics" v-if="!loading">
      <div v-if="studyYear()">
          <strong>Study Year:</strong> {{ studyYear() }}
      </div>
      <div class="is-parent tile">
        <div class="tile is-child">
          <div>
            <help text="Engagement score is an indication of the level of engagement with the system. It is calculated from the time spent in assigned books and opening of pushed resources, as well as if the student has an up to date version of miEbooks." />
            <charts :options="enagementChartOptions"></charts>
          </div>
        </div>
        <div class="tile is-child">
          <div>
            <help text="This chart shows the level of engagement per group, and the split between the score for the time spent in books and opened resources. Note that if a group has no books assigned, only the opened resources score will count, and vice versa." />
            <charts :options="groupScoresChartOptions"></charts>
          </div>
        </div>
      </div>
      <div class="is-parent tile">
        <div class="tile is-child">
          <div>
            <div class="inline has-text-centered">
              <div>
                  <p class="heading">Highlights</p>
                  <span v-if="data.studentData" class="title">{{ data.studentData.highlights ? data.studentData.highlights : "0" }}</span>
              </div>
            </div>
            <div class="inline has-text-centered">
                <div>
                    <p class="heading">Annotations</p>
                    <p class="title">{{ annotations() ? annotations() : "0" }}</p>
                </div>
            </div>
            <div class="inline has-text-centered">
                <div>
                    <p class="heading">Flashcard Decks</p>
                    <p v-if="data.studentData" class="title">{{ data.studentData && data.studentData.privateFlashcardDecks ? data.studentData.privateFlashcardDecks : "0" }}</p>
                </div>
            </div>
            <div class="inline has-text-centered">
                <div>
                    <p class="heading">Flashcards</p>
                    <span v-if="data.studentData" class="title">{{ data.studentData.privateFlashcards ? data.studentData.privateFlashcards : "0" }}</span>
                </div>
            </div>
            <div class="inline has-text-centered">
                <div>
                    <p class="heading">Summaries</p>
                    <span v-if="data.studentData" class="title">{{ data.studentData.summaries ? data.studentData.summaries : "0"}}</span>
                </div>
            </div>
            <div class="inline has-text-centered">
                <div>
                    <p class="heading">App Version</p>
                    <span class="has-text-black no-breaks">
                        <span v-if="data.studentData && data.studentData.appVersion" class="title version-number">{{ data.studentData.appVersion }}</span>
                        <span v-if="data.studentData && data.studentData.platform == 'ANDROID'" class="icon">
                            <i class="fab fa-2x fa-android"></i>
                        </span>
                        <span v-if="data.studentData && data.studentData.platform == 'WINDOWS'" class="icon platform">
                            <i class="fab fa-2x fa-windows"></i>
                        </span>
                        <span v-if="data.studentData && data.studentData.platform == 'IOS'" class="is-size-3">iOS</span>
                        <span v-if="data.studentData && data.studentData.platform == 'MACOS'" class="is-size-3">MacOS</span>
                    </span>
                </div>
            </div>
          </div>
        </div>
      </div>
      <div class="tile is-parent">
        <div class="has-text-centered tile is-child">
          <div v-if="data.studentUnlocks && data.studentUnlocks.actual && data.studentUnlocks.expected">
            <help text="Shows book unlock progress. Note that the value is only updated once every night, so it shows progress up to the previous day." />
            <span>{{ data.studentUnlocks.actual }} / {{ data.studentUnlocks.expected }} books unlocked</span>
            <div style="padding: 10px">
              <progress class="progress is-info" :max="data.studentUnlocks.expected" :value="data.studentUnlocks.actual"></progress>
            </div>
          </div>
        </div>
      </div>
      <div class="tile is-parent">
        <div class="tile is-child">
          <div>
            <help text="The number of interactive events performed each day by this user, for the last 30 days." />
            <charts :options="dailyActivityChartOptions"></charts>
          </div>
        </div>
        <div class="tile is-child">
          <div>
            <help text="The average number of interactive events performed each hour by this user, calculated over the last 30 days." />
            <charts :options="hourlyActivityChartOptions"></charts>
          </div>
        </div>
      </div>      
      <div class="tile is-parent">
        <div class="tile is-child">
          <div>
            <help text="Shows list of current books, when they were last opened and time spent in each. Time is shown in <i>hours:minutes</i> format. <i>Book Days</i> (if shown) is the number of unique dates on which the book was opened. <i>Time In Resources</i> (if shown) is the portion of <i>Time In Book</i> that was spent in resources opened from within the book." />
            <h1>Book Activities</h1>
            <div class="horiz-scroll">
              <table id="tst" class="table is-fullwidth">
                  <thead class="thead-default">
                      <tr>
                          <th>
                              Book
                          </th>
                          <th class="has-text-centered">
                              Last&nbsp;Active
                          </th>
                          <th class="has-text-centered is-hidden-mobile">
                              Book&nbsp;Days
                          </th>
                          <th class="has-text-centered is-hidden-mobile">Time&nbsp;In Resources</th>
                          <th class="has-text-centered">
                              Time&nbsp;In Book
                          </th>
                          <th class="has-text-centered"></th>
                          <th class="has-text-centered">
                              Group&nbsp;Avg
                          </th>
                      </tr>
                  </thead>
                  <tbody>
                      <tr v-for="(row, i) in data.sortedActivities" v-bind:key="'sortedActivity' + i">
                          <td>{{ row.title }}</td>
                          <td class="has-text-centered">
                              {{ (row.lastActivity == '1970-01-01' ? 'never' : row.lastActivity) | toNoBreakHyphens }}
                          </td>
                          <td class="has-text-centered is-hidden-mobile">{{ row.bookDays }}</td>
                          <td class="has-text-centered is-hidden-mobile">
                              <span v-if="row.inResourceDuration >= 60">
                                  {{ min(row.inResourceDuration) | toHourMin }}
                              </span>
                          </td>
                          <td class="has-text-centered">
                              {{ min(row.duration) | toHourMin }}
                          </td>
                          <td v-if="min(row.duration) > min(row.groupAvg)" class="has-text-centered has-text-success">&gt;</td>
                          <td v-if="min(row.duration) == min(row.groupAvg)" class="has-text-centered has-text-success">=</td>
                          <td v-if="min(row.duration) < min(row.groupAvg)" class="has-text-centered has-text-danger">&lt;</td>
                          <td class="has-text-centered">{{ min(row.groupAvg) | toHourMin }}</td>
                      </tr>
                  </tbody>
                  <tfoot>
                      <tr>
                          <th>Totals</th>
                          <th></th>
                          <th class="is-hidden-mobile"></th>
                          <th class="is-hidden-mobile"></th>
                          <th class="has-text-centered">{{ min(data.totalTimeInBook) | toHourMin }}</th>
                          <th class="has-text-centered"></th>
                          <th class="has-text-centered">{{ min(data.totalGradeAvg) | toHourMin }}</th>
                      </tr>
                  </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="tile is-parent">
        <div class="tile is-child">
          <div>
            <help text="This is a summary of resources pushed and opened, both for <i>learning paths</i> or simply pushed into a book, as well as personal resources. Use the tabs to switch between the two kinds. In the learning paths tab, click on the summaries to see more detailed breakdowns of those sections." />
            <ResourceActivities 
              v-if="data.lpResources && data.studentResources && data.privateResources" 
              :intialResources="data.lpResources" 
              :bookResources="data.studentResources" 
              :privateResources="data.privateResources"
              :assessments="data.assessments"
              :assessmentsDetails="data.assessmentsDetails"
              />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.horiz-scroll {
    overflow-x: scroll;
}

.inline {
    display: inline-block;
    padding: 10px 10px 0;
}

.has-text-centered {
  text-align: center;
  margin: 0 auto;
}

.tile.is-child > div {
  background: white;
  box-shadow: 0 2px 3px rgba(10,10,10,.1), 0 0 0 1px rgba(10,10,10,.1);
  padding: 10px;
  margin: 0px 10px;
}

td {
  font-size: 10pt;
}

.analytics h1 {
  font-size: 1.1rem;
  padding-bottom: 10px;
  border-bottom: 2px solid #f0f0f0;
}

a:hover {
  text-decoration: none;
}
</style>


<script>
import {enagementChartOptions, groupScoresChartOptions, dailyActivityChartOptions, hourlyActivityChartOptions} from './chart-options.js'
import Highcharts from 'highcharts'
import {Chart} from 'highcharts-vue'
import highchartsMore from 'highcharts/highcharts-more';
import solidGauge from 'highcharts/modules/solid-gauge';
import ResourceActivities from './ResourceActivities';
import Help from './HelpTooltip';
import storeHelpers from "@/utils/storeHelpers.js";

highchartsMore(Highcharts);
solidGauge(Highcharts);

const _ = require('lodash');
const moment = require('moment');
require('moment-timezone');
moment.tz.setDefault('Africa/Johannesburg');

import Vue from 'vue'
import utils from '@/utils/utils.js';
import userUtils from '@/utils/userUtils.js';
import analytics from '@/utils/analytics.js';
import error from '@/utils/error.js'

export default {
  name: 'Analytics',
  components: {
      highcharts: Chart,
      ResourceActivities, 
      help: Help
  },
  data: function () {
    var t = this;

    return {
      loading: true,
      ee: {},
      error: "",
      enagementChartOptions: enagementChartOptions,
      groupScoresChartOptions: groupScoresChartOptions,
      dailyActivityChartOptions: dailyActivityChartOptions,
      hourlyActivityChartOptions: hourlyActivityChartOptions
    }
  },
  mounted: function () {
    storeHelpers.checkActive(this);
    userUtils.checkAuth(this, this.getLatestAnalytics);
    analytics.studentStats(this, window.deviceEventsProtobuf);
  },
  filters: {
    toNoBreakHyphens: function (value) {
      return value.replace(/\-/g, '\u2011');
    },
    ToHourMin: function (value) {
      var hour = 0;
      if (value >= 60) {
        var hour = Math.floor(value / 60);
        var minute = value - (hour * 60);
        if (minute < 10) {
          return hour + ":0" + minute;
        } else {
          return hour + ":" + minute;
        }
      } else if (value < 10) {
        return "0:0" + value;
      } else {
        return "0:" + value;
      }
    }
  },
  methods: {
    min(millis) {
      return Math.round(millis / 60);
    },
    getLatestAnalytics: function (username) {
      var t = this;
      t.loading = true;

      // var url = t.$config.get('dns') + "/analytics/api/app/students/"+utils.uriEncode(username)+"?deviceId=" + t.$store.state.uniqueId;
      var url = t.$config.get('dns') + "/redstone/api/student/current";

      axios.get(url).then(response => {
        // console.log(response.data)
        t.data = response.data

        var engagementScore = Math.round(t.data.studentData.engagement * 1000) / 10;
        t.enagementChartOptions.series[0].data = [engagementScore];

        t.updateGroupScoresChart();

        // null
        if (t.data.studentUnlocks) {
          t.data.studentUnlocks.expected = t.data.studentUnlocks.length;
          t.data.studentUnlocks.actual = t.data.studentUnlocks.filter(i => i.lastUnlock).length;
        }
        else {
          t.data.studentUnlocks = {}
          t.data.studentUnlocks.expected = undefined;
          t.data.studentUnlocks.actual = undefined;
        }

        // daily activity
        if(t.data.studentDailyActivity.length > 0) {
          var days = [];
          var actions = [];
          for (let a of t.data.studentDailyActivity) {
            days.push(a.day);
            actions.push(a.actions);
          }

          t.dailyActivityChartOptions.xAxis.categories = days;
          t.dailyActivityChartOptions.series[0].data = actions;
        }

        // hourly activity
        if(t.data.studentHourlyActivity) {
          var hours = [];
          var actions = [];
          for (let h = 0; h < 24; h++) {
            hours.push(h + ":00");
            let p = t.data.studentHourlyActivity.find(x => x.hour === h);
            actions.push(p && p.actions || 0);
          }

          t.hourlyActivityChartOptions.xAxis.categories = hours;
          t.hourlyActivityChartOptions.series[0].data = actions;
        }

        // book activities
        t.data.sortedActivities = t.data.studentTimeInBook;
        t.data.totalTimeInBook = 0;
        t.data.totalGradeAvg = 0;
        if (t.data.sortedActivities) {
          t.data.sortedActivities.sort((a, b) => {
            if (a[this.ordering] > b[this.ordering]) {
              return 1;
            }
            else if (a[this.ordering] < b[this.ordering]) {
              return -1;
            }
            else if (a.title > b.title) {
              return 1;
            }
            else if (a.title < b.title) {
              return -1;
            }
            else {
              return 0;
            }
          });

          t.data.sortedActivities.forEach(element => {
            t.data.totalTimeInBook = t.data.totalTimeInBook + element.duration;
            t.data.totalGradeAvg = t.data.totalGradeAvg + element.groupAvg;
          });
        }

        t.loading = false;
      })
      .catch(e => {
          // console.log(e)
          error.check401And498(t, e);
          t.loading = false;
          t.error = error.logAndGetError(t, e);
      })
    },
    studyYear: function () {
      return this.data && this.data.studentData && utils.gradeLookup(this.data.studentData.grade);
    },
    annotations() {
      if (this.data.studentData) {
        return (this.data.studentData.notes ? this.data.studentData.notes : 0) + (this.data.studentData.sketches ? this.data.studentData.sketches : 0) + (this.data.studentData.drawings ? this.data.studentData.drawings : 0);
      }
    },
    updateGroupScoresChart() {
      const groups = this.data.studentGroups;

      if (groups.length > 0) {
        const title = `Group Score${groups.length > 1 ? 's' : ''} for ${groups.length} Group${groups.length > 1 ? 's' : ''}`;
        const w = groups.filter(x => x.bookScore || x.resourceScore)
          .map(x => {
            if (x.bookScore === x.score) {
              return {
                name: x.name,
                bookScore: Math.round(x.bookScore * 100) || 0,
                resourceScore: 0,
                score: Math.round(x.score * 100) || 0
              };
            } else if (x.resourceScore === x.score) {
              return {
                name: x.name,
                bookScore: 0,
                resourceScore: Math.round(x.resourceScore * 100) || 0,
                score: Math.round(x.score * 100) || 0
              };
            } else {
              return {
                name: x.name,
                bookScore: Math.round(x.bookScore * 60) || 0,
                resourceScore: Math.round(x.resourceScore * 40) || 0,
                score: Math.round(x.score * 100) || 0
              };
            }
          })
          .sort((a, b) => {
            if (a.name < b.name) return -1;
            else if (a.name > b.name) return 1;
            else return 0;
          });
        const categories = w.map(x => x.name);
        const series = [{
          name: 'Book Score',
          data: w.map(x => x.bookScore)
        }, {
          name: 'Resource Score',
          data: w.map(x => x.resourceScore)
        }];

        this.groupScoresChartOptions.series = series;
        this.groupScoresChartOptions.xAxis.categories = categories;
      }
    }
  }
}
</script>